import { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const NDA_TEXT = `NON-DISCLOSURE AGREEMENT
(Kasunduan sa Hindi Pagbubunyag ng Impormasyon)

Ang Kasunduang ito ay ginawa sa pagitan ng:

SusTek Pte Ltd, isang rehistradong negosyo / organisasyon sa ilalim ng batas ng Pilipinas at Singapore, na may tanggapan sa Level 24, CapitaGreen, 138 Market Street, Singapore, 048946, na tatawaging "ORGANISASYON";

at ang Field Coordinator na pumirma sa ibaba.

1. Layunin ng Kasunduan
Layunin ng Kasunduang ito na protektahan ang lahat ng sensitibo, kumpidensyal, at pagmamay-aring impormasyon ng ORGANISASYON, kabilang ang mga estratehiya, sistema, plano, at iba pang intellectual property, laban sa hindi awtorisadong paggamit o pagbubunyag.

2. Kahulugan ng Kumpidensyal na Impormasyon
Ang "Kumpidensyal na Impormasyon" ay tumutukoy sa lahat ng impormasyon na ibinahagi sa Field Coordinator, pasalita man, nakasulat, digital, o anumang anyo, kabilang ngunit hindi limitado sa:
• Mga business plan at estratehiya
• Mga detalye ng platform, system architecture, proseso, at workflow
• Mga training materials, manuals, at internal documents
• Mga financial records at projections
• Listahan ng kliyente, partners, suppliers, at contacts
• Marketing strategies at expansion plans
• Mga prototype, design, source code, concepts, at imbensyon
• Anumang hindi pa inilalabas sa publiko na impormasyon ng ORGANISASYON

Ang lahat ng nabanggit ay saklaw ng Intellectual Property Code of the Philippines (RA 8293) at iba pang kaugnay na batas.

3. Mga Obligasyon ng Field Coordinator
Ang Field Coordinator ay sumasang-ayon na:
• Panatilihing lihim ang lahat ng Kumpidensyal na Impormasyon.
• Hindi ibunyag, ipamahagi, o ipasa ang anumang impormasyon sa sinumang tao o entidad nang walang nakasulat na pahintulot ng ORGANISASYON.
• Gamitin lamang ang impormasyon para sa opisyal na tungkulin sa loob ng ORGANISASYON.
• Hindi kopyahin, i-reproduce, o i-record ang anumang kumpidensyal na materyales para sa personal na pakinabang.
• Isauli o burahin ang lahat ng dokumento at files kapag natapos ang kanyang serbisyo.

4. Saklaw ng Intellectual Property
Lahat ng ideya, imbensyon, disenyo, materyales, content, proseso, system improvement, at anumang output na mabubuo ng Field Coordinator habang siya ay konektado sa ORGANISASYON ay ituturing na pagmamay-ari ng ORGANISASYON.

5. Hindi Pag-kompetensya at Hindi Pag-solicit
Habang may ugnayan sa ORGANISASYON at sa loob ng dalawang (2) taon matapos ang pagtatapos ng serbisyo, ang Field Coordinator ay hindi dapat gumawa o tumulong sa paggawa ng kaparehong platform o negosyo na direktang kakompetensya, o hikayatin ang mga kliyente at partners para sa personal na interes.

6. Data Privacy
Ang Field Coordinator ay obligadong sumunod sa Data Privacy Act of 2012 (RA 10173) at tiyakin na ang anumang personal data na kanyang maa-access ay mananatiling ligtas.

7. Tagal ng Bisa
Ang obligasyon ng pagiging kumpidensyal ay mananatiling epektibo kahit matapos ang relasyon o serbisyo bilang Field Coordinator.

8. Mga Parusa at Pananagutan
Ang paglabag sa Kasunduang ito ay maaaring magresulta sa agarang pagtatapos ng kontrata, pagsasampa ng civil case, at kaukulang kaso sa ilalim ng Intellectual Property Code.

9. Buong Kasunduan
Ang dokumentong ito ang bumubuo sa kabuuan ng kasunduan ukol sa pagiging kumpidensyal sa pagitan ng mga partido.

10. Batas na Sasaklaw
Ang Kasunduang ito ay paiiralin alinsunod sa mga batas ng Republika ng Pilipinas.`;

export default function NDASign({ onSigned }) {
  const { user, checkAuth } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");
  const [signing, setSigning] = useState(false);

  const handleSign = async () => {
    if (!agreed || !signature.trim()) { toast.error("Please agree and type your full name as digital signature"); return; }
    setSigning(true);
    try {
      await api.post("/coordinator/sign-nda", { signature: signature.trim() });
      toast.success("NDA signed successfully. Welcome aboard!");
      await checkAuth();
      if (onSigned) onSigned();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to sign NDA"); }
    setSigning(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="nda-page">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-6">
          <ShieldCheck className="w-10 h-10 text-primary mx-auto mb-2" />
          <h1 className="font-heading text-2xl font-bold">Non-Disclosure Agreement</h1>
          <p className="text-sm text-muted-foreground">Please read and sign before proceeding</p>
        </div>

        <div className="bg-white border rounded-xl p-6 mb-6 max-h-[50vh] overflow-y-auto text-sm whitespace-pre-wrap leading-relaxed" data-testid="nda-content">
          {NDA_TEXT}
        </div>

        <div className="bg-white border rounded-xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox checked={agreed} onCheckedChange={setAgreed} id="nda-agree" data-testid="nda-agree" />
            <label htmlFor="nda-agree" className="text-sm cursor-pointer">
              I have read, understood, and agree to the terms of this Non-Disclosure Agreement. I acknowledge that this is a legally binding document.
            </label>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">Digital Signature (Type your full name)</label>
            <Input value={signature} onChange={e => setSignature(e.target.value)}
              placeholder={user?.name || "Your full legal name"} className="font-mono italic text-lg"
              data-testid="nda-signature" />
          </div>

          <Button onClick={handleSign} disabled={signing || !agreed || !signature.trim()}
            className="w-full h-12 rounded-full font-heading uppercase" data-testid="nda-sign-btn">
            {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
}
